// Shared helpers for the 4-test pathway results.
// • Anonymous users → localStorage (instant), keyed under `anon`
// • Logged-in users → localStorage keyed by user_id + synced to /api/test-results
//
// Reads always merge server + local so a user who started anonymously then
// logged in still sees their old results — BUT a brand-new account on a
// shared browser never inherits the previous user's saved results, because
// every key is namespaced by user_id (or 'anon').

import axios from 'axios';

const BASE = 'learnixta_test_results';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = () => ({ pathway: null, tests: {}, completedAt: null });

function currentUserScope() {
  // Best-effort sync read of the user from a global the auth provider sets
  // (see /app/frontend/src/lib/auth.js). Falls back to 'anon'.
  try {
    const cached = window.__learnixta_user_id;
    if (cached) return cached;
  } catch { /* ignore */ }
  return 'anon';
}

function keyFor(scope) {
  return `${BASE}__${scope || currentUserScope()}`;
}

export function getResults() {
  try {
    const raw = localStorage.getItem(keyFor());
    if (!raw) return empty();
    const parsed = JSON.parse(raw);
    return { ...empty(), ...parsed };
  } catch {
    return empty();
  }
}

export function saveTestResult(pathway, testId, payload) {
  const data = getResults();
  data.pathway = pathway;
  data.tests = { ...data.tests, [testId]: { ...payload, savedAt: new Date().toISOString() } };
  data.completedAt = new Date().toISOString();
  try { localStorage.setItem(keyFor(), JSON.stringify(data)); } catch { /* Quiz remains usable when storage is blocked. */ }

  return data;
}

export async function hydrateFromServer() {
  try {
    const { data } = await axios.get(`${API}/test-results`, { withCredentials: true });
    if (!data || (!data.tests && !data.pathway)) return getResults();
    const merged = {
      pathway: data.pathway || null,
      tests: data.tests || {},
      completedAt: data.completedAt || null,
    };
    localStorage.setItem(keyFor(), JSON.stringify(merged));
    return merged;
  } catch {
    return getResults();
  }
}

export function clearResults() {
  // Clear THIS user's key plus the legacy unscoped one so old installs heal.
  localStorage.removeItem(keyFor());
  localStorage.removeItem(BASE);
  axios.delete(`${API}/test-results`, { withCredentials: true }).catch(() => {});
}

// Called from auth provider whenever the active user changes.
export function clearAllLocalUserData() {
  try {
    // 1. Wipe the global legacy key (pre-user-scoping)
    localStorage.removeItem(BASE);
    // 2. Wipe any cc_v3_progress_* (Career Clarity progress)
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('cc_v3_progress_') || k.startsWith('project_') || k === 'learnixta_chat_session_v1') {
        localStorage.removeItem(k);
      }
    });
  } catch { /* ignore */ }
}
